import { useCallback, useMemo } from 'react';

import { DatePicker, Form } from 'antd';

import subDays from 'date-fns/subDays';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { FieldProps } from 'formik';
import { getIn } from 'formik';
import { formatDate } from 'helpers/date';
import { Hint } from 'v2/components/Shared/Hint';
import { parseDdMmYyyy } from 'v2/formatters/date';

const { RangePicker } = DatePicker;

const DATE_FORMAT = 'DD/MM/YYYY';

type PresetItem = { id: string; text: string; start: Date; end: Date };

const today = dayjs().startOf('day');

const defaultPresets: PresetItem[] = [
  { id: 'today', text: 'Hoje', start: today.toDate(), end: today.toDate() },
  { id: 'week', text: 'Últimos 7 dias', start: subDays(today.toDate(), 7), end: today.toDate() },
  { id: '2weeks', text: 'Últimos 15 dias', start: subDays(today.toDate(), 15), end: today.toDate() },
  { id: 'month', text: 'Últimos 30 dias', start: subDays(today.toDate(), 30), end: today.toDate() }
];

const defaultPresetsLonger: PresetItem[] = [
  { id: 'week', text: 'Últimos 7 dias', start: subDays(today.toDate(), 7), end: today.toDate() },
  { id: '1month', text: 'Último mês', start: subDays(today.toDate(), 30), end: today.toDate() },
  { id: '3month', text: 'Últimos 3 meses', start: subDays(today.toDate(), 90), end: today.toDate() },
  { id: '6month', text: 'Últimos 6 meses', start: subDays(today.toDate(), 180), end: today.toDate() }
];

const toDayjs = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }

  const parsed = dayjs(parseDdMmYyyy(value));
  return parsed.isValid() ? parsed : null;
};

interface IProps extends FieldProps {
  label?: string;
  isPresetLonger?: boolean;
  isOutsideRange?: (day: Date) => boolean;
  customPreset?: PresetItem[];
  showPreset?: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
}

export const DateRangePickerFormField = ({
  form,
  label,
  field,
  isPresetLonger,
  customPreset,
  showPreset = true,
  maxDate,
  disabled
}: IProps) => {
  const value = useMemo<[Dayjs | null, Dayjs | null]>(
    () => [toDayjs(field.value?.[0]), toDayjs(field.value?.[1])],
    [field.value]
  );

  const fieldError = getIn(form.errors, field.name) as string | undefined;

  const presets = useMemo(() => {
    if (!showPreset) {
      return undefined;
    }

    const list = customPreset || (isPresetLonger ? defaultPresetsLonger : defaultPresets);

    return list.map(({ text, start, end }) => ({
      label: text,
      value: [dayjs(start), dayjs(end)] as [Dayjs, Dayjs]
    }));
  }, [showPreset, customPreset, isPresetLonger]);

  const handleChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      if (!dates?.[0]?.isValid() || !dates?.[1]?.isValid()) {
        form.setFieldValue(field.name, [null, null], true);
        return;
      }

      form.setFieldValue(
        field.name,
        [formatDate(dates[0].toDate(), 'dd/MM/yyyy'), formatDate(dates[1].toDate(), 'dd/MM/yyyy')],
        true
      );
    },
    [form, field.name]
  );

  return (
    <Hint inputName={field.name}>
      <Form.Item
        className='relative z-(--uizz-antd-z-index-base) mb-0 w-full'
        label={label}
        layout='vertical'
        validateStatus={fieldError ? 'error' : undefined}
      >
        <RangePicker
          value={value}
          onChange={handleChange}
          format={{ format: DATE_FORMAT, type: 'mask' }}
          maxDate={dayjs(maxDate || new Date())}
          presets={presets}
          allowClear
          disabled={disabled}
          status={fieldError ? 'error' : undefined}
          placeholder={['Data inicial', 'Data final']}
          className='w-full'
        />
      </Form.Item>
    </Hint>
  );
};
